'use client';

import { IAuthorListItem } from '@/api/types/blog';
import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Arrow_down } from '@zeelool/icon';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useContext, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTranslations } from '@/i18n/use-translations';
interface AuthorCardProps {
  author: IAuthorListItem;
  onAuthorClick?: (author: IAuthorListItem, index: number) => void;
  index: number;
  onExposure?: (author: IAuthorListItem, index: number) => void;
  isWeb?: boolean;
}

const AuthorCard: React.FC<AuthorCardProps> = ({
  author,
  onAuthorClick,
  index,
  onExposure,
  isWeb = false,
}) => {
  const t = useTranslations('blog.components');
  const router = useRouter();
  const { deviceType } = useContext(ResponsiveContext);
  const inAppH5 = [EPlatform.IOS, EPlatform.ANDROID].includes(deviceType);

  const [authorRef, inView] = useInView({
    threshold: 0.8,
    triggerOnce: true,
  });
  useEffect(() => {
    if (inView) {
      onExposure(author, index);
    }
  }, [inView]);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // 执行埋点
    if (onAuthorClick) {
      onAuthorClick(author, index);
    }
    // 执行跳转
    if (inAppH5) {
      router.push(`/blog/authors/${author.author_path}`);
    } else {
      window.open(`/blog/authors/${author.author_path}`, '_blank');
    }
  };

  const formatText = (text?: string) => {
    return text?.replace(/\n/g, '<br>') || '';
  };

  return (
    <Link
      prefetch={false}
      href={`/blog/authors/${author.author_path}`}
      onClick={(e) => handleClick(e)}
      className={`flex items-start justify-between bg-white ${isWeb ? 'py-40 px-32' : 'p-[20px_12px]'}`}
      aria-label={author.name}
      ref={authorRef}
    >
      <div
        className={`rounded-full overflow-hidden shrink-0 ${isWeb ? 'mt-[5px] w-[150px] h-[150px]' : 'w-60 h-60'}`}
      >
        <Image
          src={author.head_portrait_url || '/images/default-avatar.png'}
          alt={author.name}
          width={isWeb ? 150 : 60}
          height={isWeb ? 150 : 60}
          className="block w-full h-full object-cover"
        />
      </div>
      <div className={`flex-1 ${isWeb ? 'ml-40' : 'ml-16'}`}>
        <h3
          className={`font-bb ${isWeb ? 'text-20' : 'text-14'}`}
          dangerouslySetInnerHTML={{ __html: formatText(author.name) }}
        />
        {author.occupation && (
          <p
            className={`mt-8 ${isWeb ? 'text-16' : 'text-12'}`}
            dangerouslySetInnerHTML={{ __html: formatText(author.occupation) }}
          />
        )}
        {author.introduction && (
          <p
            className={`font-ir ${isWeb ? 'text-14 mt-24' : 'mt-8 text-12 leading-18'}`}
            dangerouslySetInnerHTML={{ __html: formatText(author.introduction) }}
          />
        )}
        <div className={`flex items-center ${isWeb ? 'mt-24 text-14' : 'mt-16 text-12'}`}>
          <div className="underline">{t('viewArticles')}</div>
          <Arrow_down className={`ml-8 rotate-[-90deg] ${isWeb ? 'text-16' : 'text-12'}`} />
        </div>
      </div>
    </Link>
  );
};

export default AuthorCard;
